import { FC, useEffect, useMemo, useState } from 'react'

import { useCurrentModel } from '~/hooks'
import { useProductSizeTable } from '~/hooks/useProductSizeTable'
import { TSizeTableRow } from '~/hooks/useProductSizeTable/types'

import { useActiveProductsContext } from '~/context/ActiveProducts'
import { useNavigation } from '~/context/Navigation'

import { Icon } from '~/components/Icon'
import { IconButton } from '~/components/IconButton'
import ImagePreview from '~/components/ImagePreview'
import { LoadingDots } from '~/components/LoadingDots'
import { RoutesHeader } from '~/components/RoutesHeader'
import { SizingTable } from '~/components/SizingTable'
import { sizingMessagesColorsMap } from '~/components/SizingTable/constants'
import { ISizingData } from '~/components/SizingTable/types'

import theme from '~/theme'

import Tracking from '~/utils/tracking'
import { translate } from '~/utils/translate'

import * as Styled from './styles'

export const SizingSuggestionScreen: FC = () => {
  const { products: activeProducts } = useActiveProductsContext()
  const { navigate } = useNavigation()
  const { getCurrentModel } = useCurrentModel()

  const [selectedImage, setSelectedImage] = useState(0)

  const identifiersWithSize = useMemo(
    () =>
      activeProducts.reduce<string[]>((acc, product) => (product.has_size ? [...acc, product.identifier] : acc), []),
    [activeProducts],
  )

  const { data, loading } = useProductSizeTable({
    identifiers: identifiersWithSize,
    avatarUUID: getCurrentModel()?.id,
  })

  const haveProductOptionsWithSuggestions = useMemo(
    () => !!data?.[0]?.size.some(({ status }) => status && status !== 'REGULAR'),
    [data],
  )

  useEffect(() => {
    if (!data) return

    let perfectFit: (TSizeTableRow | undefined)[] = []
    if (haveProductOptionsWithSuggestions) {
      perfectFit = data.map(product => product.size.find(({ status }) => status === 'FIT'))
    }

    Tracking.logEvent('SIZE_CHART', {
      origin: Tracking.getLastEventData()?.origin ?? '',
      widget: true,
      ...(haveProductOptionsWithSuggestions
        ? {
            top: activeProducts.find(({ type }) => ['TOP', 'FULL'].includes(type))?.identifier,
            top_perfect_size: perfectFit.find(product => Boolean(product?.data.chest))?.data.size,
            bottom: activeProducts.find(({ type }) => type === 'BOTTOM')?.identifier,
            bottom_perfect_size: perfectFit.find(product => !product?.data.chest)?.data.size,
          }
        : {}),
    })
  }, [activeProducts, data, haveProductOptionsWithSuggestions])

  return (
    <Styled.Container data-testid="sizing-table-screen-container">
      <RoutesHeader
        title={translate(
          haveProductOptionsWithSuggestions
            ? 'SIZING_TABLE_SCREEN_TITLE_WITH_SUGGESTIONS'
            : 'SIZING_TABLE_SCREEN_TITLE',
        )}
      />

      <Styled.CloseButtonWrapper>
        <IconButton
          testID="close-widget-button"
          onClick={() => navigate('Home', { showUnavailableVitonBackdrop: true, identifiers: identifiersWithSize })}
          backgroundColor="transparent"
          icon={<Icon name="close" size="12px" color={theme.colors.primary} />}
          boxShadow={false}
        />
      </Styled.CloseButtonWrapper>

      <Styled.ContentWrapper>
        {loading && (
          <LoadingDots
            isVisible={true}
            iconPosition={{ vertical: 'CENTER' }}
            backgroundColor="white"
            color={theme.colors.primary}
            size="12px"
          />
        )}

        {data && !loading && (
          <Styled.ImageAndTableWrapper>
            <Styled.ImageButtonsWrappers>
              {data.map(({ thumbnail }, index) => (
                <Styled.ImageButton
                  data-testid={`image-button-${index + 1}`}
                  key={`image-button-${index}`}
                  selected={selectedImage === index}
                  onClick={() => setSelectedImage(index)}
                >
                  <ImagePreview url={thumbnail} fullsize={true} />
                </Styled.ImageButton>
              ))}
            </Styled.ImageButtonsWrappers>

            <Styled.TableWrapper data-testid={`table-wrapper-${selectedImage + 1}`}>
              <SizingTable data={data[selectedImage] as ISizingData} />
            </Styled.TableWrapper>

            <Styled.AdditionalInfo>
              <Styled.FitFlags>
                {data[selectedImage].size.find(item => item.status === 'FIT') && (
                  <Styled.ColoredBox>
                    <Styled.BoxFlag color={sizingMessagesColorsMap['FIT']} />
                    {translate('SIZING_TABLE_FIT_FLAG_FIT')}
                  </Styled.ColoredBox>
                )}

                {data[selectedImage].size.some(item => item.status === 'FIT') &&
                  data[selectedImage].size.some(item => item.status === 'SOME_LOOSE') && (
                    <Styled.Separator data-testid="separator">|</Styled.Separator>
                  )}

                {data[selectedImage].size.some(item => item.status === 'SOME_LOOSE') && (
                  <Styled.ColoredBox>
                    <Styled.BoxFlag color={sizingMessagesColorsMap['SOME_LOOSE']} />
                    {translate('SIZING_TABLE_FIT_FLAG_SOME_LOOSE')}
                  </Styled.ColoredBox>
                )}
              </Styled.FitFlags>

              <Styled.Unit data-testid="sizing-table-unit">
                {translate('SIZING_TABLE_UNIT_LABEL')} <Styled.UnitBold>{data[selectedImage].unit}</Styled.UnitBold>
              </Styled.Unit>
            </Styled.AdditionalInfo>
          </Styled.ImageAndTableWrapper>
        )}
      </Styled.ContentWrapper>
    </Styled.Container>
  )
}
